/**
 * Schedule Post — full-screen-overlay compose surface, Buffer-style.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  Schedule post                                        ✕   │
 *   │                                                            │
 *   │  ╭─ When ────────────────────╮  ╭─ Channels ────────────╮ │
 *   │  │ date / time pickers       │  │ ◯ IG quotologies       │ │
 *   │  ╰───────────────────────────╯  │ ◯ YT Quotologies       │ │
 *   │                                  │ … per-row toggle       │ │
 *   │  ╭─ Caption ─────────────────╮  ╰────────────────────────╯ │
 *   │  │ textarea (default text)   │                              │
 *   │  ╰───────────────────────────╯                              │
 *   │                                                             │
 *   │  ╭─ Pinterest preview (auto) ╮  ╭─ YouTube title ────────╮ │
 *   │  ╰───────────────────────────╯  ╰────────────────────────╯ │
 *   │                                                            │
 *   │                                    [Cancel] [Schedule N]   │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Per-channel adaptation mirrors src/lib/scheduling/captions.ts so the
 * operator sees exactly what the backend will send.
 */
import { AlertTriangle, Calendar, Check, X } from "lucide-react";
import { BrandDateTimePicker } from "./BrandDateTimePicker";
import { useEffect, useMemo, useState } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import {
  listSchedulingChannels,
  scheduleJobPost,
  type ChannelService,
  type SchedulingChannel,
} from "../api";
import { serviceLabel, ServiceIcon } from "./SocialIcons";
import { cardStyle } from "./styles";

// --- per-channel adaptation (mirror of src/lib/scheduling/captions.ts) ---
const PINTEREST_MAX = 500;
const LINKEDIN_MAX = 3000;
const YT_TITLE_MAX = 100;

function charCount(s: string): number {
  return Array.from(s).length;
}

function truncateClean(text: string, cap: number): string {
  if (charCount(text) <= cap) return text;
  const cp = Array.from(text);
  const window = cp.slice(0, cap).join("");
  const next = cp[cap];
  if (!next || /\s/.test(next)) return window.trimEnd();
  const para = window.lastIndexOf("\n\n");
  if (para > 0) return text.slice(0, para).trimEnd();
  const line = window.lastIndexOf("\n");
  if (line > 0) return text.slice(0, line).trimEnd();
  const sentence = [...window.matchAll(/[\.!?…؟。]\s/g)];
  if (sentence.length > 0) {
    const last = sentence[sentence.length - 1]!;
    return text.slice(0, last.index! + last[0].length).trimEnd();
  }
  const space = window.lastIndexOf(" ");
  if (space > 0) return text.slice(0, space).trimEnd();
  return cp.slice(0, cap).join("").trimEnd();
}

// General hook+CTA-preserving shortener (mirror of shortenToLimit). Any
// capped platform reuses it via its cap.
function previewShorten(caption: string, cap: number): string {
  if (charCount(caption) <= cap) return caption;
  const paragraphs = caption.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length >= 2) {
    const hook = paragraphs[0]!;
    const tail = paragraphs[paragraphs.length - 1]!;
    const combo = `${hook}\n\n${tail}`;
    if (charCount(combo) <= cap) return combo;
  }
  return truncateClean(caption, cap);
}

function previewPinterest(caption: string): string {
  return previewShorten(caption, PINTEREST_MAX);
}

function previewYouTubeTitle(caption: string): string {
  const firstLine = caption.split(/\r?\n/).map((l) => l.trim()).find((l) => l) ?? "";
  return truncateClean(firstLine, YT_TITLE_MAX);
}

interface Props {
  open: boolean;
  jobId: string;
  defaultCaption: string;
  onClose: () => void;
  onScheduled?: (when: string, count: number) => void;
}

function defaultDueLocal(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 30, 0, 0);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function SchedulePostModal({
  open,
  jobId,
  defaultCaption,
  onClose,
  onScheduled,
}: Props): React.JSX.Element | null {
  const isMobile = useIsMobile();
  const [channels, setChannels] = useState<SchedulingChannel[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Channels the operator flipped to Buffer's notification mode (phone
  // push at dueAt, finish manually). Everything else direct-publishes.
  const [notifyIds, setNotifyIds] = useState<Set<string>>(new Set());
  const [caption, setCaption] = useState(defaultCaption);
  const [ytTitle, setYtTitle] = useState("");
  const [titleDirty, setTitleDirty] = useState(false);
  const [dueLocal, setDueLocal] = useState(() => defaultDueLocal());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoadError(null);
    setCaption(defaultCaption);
    setYtTitle(previewYouTubeTitle(defaultCaption));
    setTitleDirty(false);
    setError(null);
    setNotifyIds(new Set());
    listSchedulingChannels()
      .then((cs) => {
        setChannels(cs);
        setSelectedIds(new Set(cs.map((c) => c.id)));
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : String(err)));
  }, [open, defaultCaption]);

  useEffect(() => {
    if (!titleDirty) setYtTitle(previewYouTubeTitle(caption));
  }, [caption, titleDirty]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  const dueAtISO = useMemo(() => {
    if (!dueLocal) return null;
    const local = new Date(`${dueLocal}:00`);
    if (Number.isNaN(local.getTime())) return null;
    return local.toISOString();
  }, [dueLocal]);

  const selectedChannels = useMemo(
    () => (channels ?? []).filter((c) => selectedIds.has(c.id)),
    [channels, selectedIds],
  );

  const hasPinterest = selectedChannels.some((c) => c.service === "pinterest");
  const hasYouTube = selectedChannels.some((c) => c.service === "youtube");
  const hasLinkedIn = selectedChannels.some((c) => c.service === "linkedin");
  const pinPreview = useMemo(() => previewPinterest(caption), [caption]);
  // LinkedIn caption only needs a shorten preview when it actually
  // overflows LinkedIn's 3000-char cap; otherwise it posts verbatim.
  const liOverflow = hasLinkedIn && charCount(caption) > LINKEDIN_MAX;
  const liPreview = useMemo(() => previewShorten(caption, LINKEDIN_MAX), [caption]);

  const canSubmit =
    !busy &&
    !!dueAtISO &&
    new Date(dueAtISO).getTime() > Date.now() &&
    selectedChannels.length > 0;

  const submit = async (): Promise<void> => {
    if (!canSubmit || !dueAtISO) return;
    setBusy(true);
    setError(null);
    try {
      const overrides: Array<{
        channelId: string;
        text?: string;
        title?: string;
        scheduling?: "notification";
      }> = [];
      for (const c of selectedChannels) {
        const scheduling = notifyIds.has(c.id) ? ("notification" as const) : undefined;
        if (c.service === "youtube") {
          overrides.push({ channelId: c.id, title: ytTitle.trim() || undefined, scheduling });
        } else if (scheduling) {
          overrides.push({ channelId: c.id, scheduling });
        }
      }
      const out = await scheduleJobPost(jobId, {
        dueAt: dueAtISO,
        channelIds: selectedChannels.map((c) => c.id),
        defaultText: caption,
        overrides,
      });
      const successCount = out.results.length;
      const failCount = out.failures.length;
      onScheduled?.(out.scheduledFor, successCount);
      if (failCount > 0) {
        const labels = out.failures
          .map((f) => {
            const ch = selectedChannels.find((c) => c.id === f.channelId);
            return `${ch?.name ?? f.channelId} (${f.error})`;
          })
          .join("; ");
        setError(`${successCount} scheduled, ${failCount} failed: ${labels}`);
        setBusy(false);
      } else {
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ql-schedule-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(4,6,10,.72)",
        backdropFilter: "blur(20px) saturate(140%)",
        WebkitBackdropFilter: "blur(20px) saturate(140%)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        animation: "ql-fade-in 180ms ease-out",
      }}
    >
      <style>{`
        @keyframes ql-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ql-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div
        style={{
          ...cardStyle,
          width: "min(880px, calc(100vw - 24px))",
          maxHeight: "92vh",
          overflowY: "auto",
          padding: 0,
          background: "linear-gradient(180deg, rgba(20,22,28,.96) 0%, rgba(14,16,22,.96) 100%)",
          border: "1px solid rgba(255,255,255,.10)",
          boxShadow: "0 20px 60px rgba(0,0,0,.55), 0 0 0 1px rgba(255,255,255,.04)",
          animation: "ql-rise 220ms cubic-bezier(.22,.84,.36,1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 24px",
            borderBottom: "1px solid rgba(255,255,255,.06)",
            position: "sticky",
            top: 0,
            background: "rgba(14,16,22,.85)",
            backdropFilter: "blur(8px)",
            zIndex: 2,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: "linear-gradient(135deg, #FFD500 0%, #FFA500 100%)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(255,213,0,.30)",
              }}
            >
              <Calendar size={16} strokeWidth={2.2} color="#1a1a1a" />
            </div>
            <h2 id="ql-schedule-title" style={{ margin: 0, fontSize: 15, fontWeight: 700, letterSpacing: -0.2 }}>
              Schedule post
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: "rgba(255,255,255,.04)",
              border: "1px solid rgba(255,255,255,.06)",
              color: "var(--ql-fg-muted)",
              cursor: busy ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 120ms ease",
            }}
            onMouseEnter={(e) => {
              if (!busy) (e.currentTarget.style.background = "rgba(255,255,255,.08)");
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,.04)";
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? "14px 14px 18px" : "20px 24px 24px" }}>
          {loadError && <ErrorBanner>{`Couldn't load channels: ${loadError}`}</ErrorBanner>}
          {error && <ErrorBanner>{error}</ErrorBanner>}

          {/* Schedule + caption row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) minmax(280px, 320px)",
              gap: 20,
              alignItems: "start",
            }}
          >
            <div>
              <SectionLabel>Caption · applies to all unless overridden</SectionLabel>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={11}
                style={{
                  width: "100%",
                  fontSize: 13,
                  lineHeight: 1.55,
                  padding: 14,
                  background: "rgba(0,0,0,.30)",
                  border: "1px solid rgba(255,255,255,.07)",
                  borderRadius: 12,
                  color: "var(--ql-fg)",
                  resize: "vertical",
                  fontFamily: "inherit",
                  outline: "none",
                  transition: "border-color 120ms ease",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(255,213,0,.40)")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,.07)")}
              />
              <Counter>{charCount(caption)} chars</Counter>
            </div>

            <div>
              <SectionLabel>When</SectionLabel>
              <div style={{ marginBottom: 12 }}>
                <BrandDateTimePicker value={dueLocal} onChange={setDueLocal} />
                <div style={{ fontSize: 11, color: "var(--ql-fg-muted)", marginTop: 6 }}>
                  Local time
                </div>
              </div>

              <SectionLabel>Channels · {selectedChannels.length} of {channels?.length ?? 0}</SectionLabel>
              {!channels ? (
                <SkeletonRow />
              ) : channels.length === 0 ? (
                <Hint>No channels in Buffer yet — add a network there first.</Hint>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {channels.map((c) => (
                    <ChannelRow
                      key={c.id}
                      channel={c}
                      checked={selectedIds.has(c.id)}
                      onToggle={() => {
                        const next = new Set(selectedIds);
                        if (next.has(c.id)) next.delete(c.id);
                        else next.add(c.id);
                        setSelectedIds(next);
                      }}
                      notify={notifyIds.has(c.id)}
                      onNotifyToggle={() => {
                        const next = new Set(notifyIds);
                        if (next.has(c.id)) next.delete(c.id);
                        else next.add(c.id);
                        setNotifyIds(next);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Per-platform overrides — only render the relevant ones. */}
          {(hasPinterest || hasYouTube || liOverflow) && (
            <div style={{ marginTop: 22, display: "grid", gap: 14 }}>
              {hasYouTube && (
                <PlatformBlock service="youtube">
                  <PlatformLabel service="youtube" sub="Video title (first line by default)" />
                  <input
                    type="text"
                    value={ytTitle}
                    onChange={(e) => {
                      setYtTitle(e.target.value);
                      setTitleDirty(true);
                    }}
                    placeholder="Video title"
                    maxLength={YT_TITLE_MAX}
                    style={{
                      ...dateInput,
                      width: "100%",
                      padding: "10px 12px",
                      background: "rgba(0,0,0,.25)",
                      border: "1px solid rgba(255,255,255,.08)",
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                  />
                  <Counter>{charCount(ytTitle)} / {YT_TITLE_MAX}</Counter>
                </PlatformBlock>
              )}

              {hasPinterest && (
                <PlatformBlock service="pinterest">
                  <PlatformLabel service="pinterest" sub={`Pinterest preview · auto-shortened to ${PINTEREST_MAX} chars`} />
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      padding: 12,
                      background: "rgba(0,0,0,.25)",
                      border: "1px solid rgba(255,255,255,.06)",
                      borderRadius: 10,
                      color: "var(--ql-gray-200)",
                      maxHeight: 180,
                      overflowY: "auto",
                    }}
                  >
                    {pinPreview}
                  </div>
                  <Counter>{charCount(pinPreview)} / {PINTEREST_MAX}</Counter>
                </PlatformBlock>
              )}

              {liOverflow && (
                <PlatformBlock service="linkedin">
                  <PlatformLabel service="linkedin" sub={`LinkedIn preview · auto-shortened to ${LINKEDIN_MAX} chars`} />
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      padding: 12,
                      background: "rgba(0,0,0,.25)",
                      border: "1px solid rgba(255,255,255,.06)",
                      borderRadius: 10,
                      color: "var(--ql-gray-200)",
                      maxHeight: 180,
                      overflowY: "auto",
                    }}
                  >
                    {liPreview}
                  </div>
                  <Counter>{charCount(liPreview)} / {LINKEDIN_MAX}</Counter>
                </PlatformBlock>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? 10 : 0,
            padding: isMobile ? "12px 14px" : "14px 24px",
            borderTop: "1px solid rgba(255,255,255,.06)",
            background: "rgba(10,12,18,.60)",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(8px)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {selectedChannels.slice(0, 6).map((c) => (
              <ServiceIcon key={c.id} service={c.service} size={22} />
            ))}
            {selectedChannels.length > 6 && (
              <span style={{ fontSize: 11, color: "var(--ql-fg-muted)" }}>+{selectedChannels.length - 6}</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} disabled={busy} style={secondaryBtn(busy)}>
              Cancel
            </button>
            <button onClick={submit} disabled={!canSubmit} style={primaryBtn(canSubmit)}>
              {busy
                ? "Scheduling…"
                : `Schedule ${selectedChannels.length} post${selectedChannels.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- styled helpers (local — extracting prematurely costs more than it saves) ---

const dateInput: React.CSSProperties = {
  background: "rgba(0,0,0,.30)",
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: 8,
  color: "var(--ql-fg)",
  padding: "6px 10px",
  fontSize: 13,
  outline: "none",
  fontFamily: "inherit",
  flex: 1,
  colorScheme: "dark",
};

function primaryBtn(active: boolean): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 16px",
    borderRadius: 10,
    fontSize: 12.5,
    fontWeight: 700,
    color: "#1a1a1a",
    background: "linear-gradient(135deg, #FFD500 0%, #FFA500 100%)",
    border: "none",
    cursor: active ? "pointer" : "not-allowed",
    opacity: active ? 1 : 0.5,
    boxShadow: active ? "0 4px 14px rgba(255,213,0,.30)" : "none",
    transition: "all 140ms ease",
  };
}

function secondaryBtn(busy: boolean): React.CSSProperties {
  return {
    padding: "9px 14px",
    borderRadius: 10,
    fontSize: 12.5,
    fontWeight: 600,
    background: "rgba(255,255,255,.04)",
    color: "var(--ql-gray-200)",
    border: "1px solid rgba(255,255,255,.08)",
    cursor: busy ? "not-allowed" : "pointer",
    opacity: busy ? 0.5 : 1,
    transition: "background 120ms ease",
  };
}

function SectionLabel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        color: "var(--ql-yellow)",
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function Counter({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ fontSize: 10.5, color: "var(--ql-fg-muted)", marginTop: 6, letterSpacing: 0.3 }}>
      {children}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div style={{ fontSize: 12, color: "var(--ql-fg-muted)" }}>{children}</div>;
}

function SkeletonRow(): React.JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 44,
            background: "linear-gradient(90deg, rgba(255,255,255,.03) 0%, rgba(255,255,255,.06) 50%, rgba(255,255,255,.03) 100%)",
            backgroundSize: "200% 100%",
            borderRadius: 10,
            animation: "ql-shimmer 1.4s ease-in-out infinite",
          }}
        />
      ))}
      <style>{`@keyframes ql-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
    </div>
  );
}

function ErrorBanner({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "11px 13px",
        marginBottom: 16,
        background: "linear-gradient(135deg, rgba(248,113,113,.10), rgba(248,113,113,.06))",
        border: "1px solid rgba(248,113,113,.30)",
        borderRadius: 10,
        color: "#FCA5A5",
        fontSize: 12.5,
        lineHeight: 1.55,
      }}
    >
      <AlertTriangle size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
}

function ChannelRow({
  channel,
  checked,
  onToggle,
  notify,
  onNotifyToggle,
}: {
  channel: SchedulingChannel;
  checked: boolean;
  onToggle: () => void;
  /** Buffer publishing mode for this channel: true = notification
   * (phone push at dueAt, finish manually), false = direct publish. */
  notify: boolean;
  onNotifyToggle: () => void;
}): React.JSX.Element {
  return (
    <button
      onClick={onToggle}
      aria-pressed={checked}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        background: checked ? "rgba(255,213,0,.05)" : "rgba(255,255,255,.02)",
        border: `1px solid ${checked ? "rgba(255,213,0,.30)" : "rgba(255,255,255,.06)"}`,
        borderRadius: 10,
        cursor: "pointer",
        textAlign: "left",
        width: "100%",
        transition: "all 120ms ease",
        color: "var(--ql-fg)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = checked ? "rgba(255,213,0,.50)" : "rgba(255,255,255,.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = checked ? "rgba(255,213,0,.30)" : "rgba(255,255,255,.06)";
      }}
    >
      <ServiceIcon service={channel.service} size={28} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ql-fg)", lineHeight: 1.2 }}>
          {channel.name}
        </div>
        <div style={{ fontSize: 10.5, color: "var(--ql-fg-muted)", letterSpacing: 0.3, marginTop: 1 }}>
          {serviceLabel(channel.service)}
        </div>
      </div>
      <span
        role="switch"
        aria-checked={notify ? "true" : "false"}
        title={
          notify
            ? "Notification: Buffer sends a phone push at the scheduled time; you finish the post in the app. Click for direct publish."
            : "Direct publish: Buffer posts automatically at the scheduled time. Click for a phone notification instead."
        }
        onClick={(e) => {
          e.stopPropagation();
          onNotifyToggle();
        }}
        style={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          padding: "3px 8px",
          borderRadius: 999,
          whiteSpace: "nowrap",
          cursor: "pointer",
          border: `1px solid ${notify ? "rgba(240,198,116,.35)" : "rgba(255,255,255,.10)"}`,
          background: notify ? "rgba(240,198,116,.08)" : "rgba(255,255,255,.03)",
          color: notify ? "#F0C674" : "var(--ql-fg-muted)",
          transition: "all 120ms ease",
          flexShrink: 0,
          // The mode only applies to channels that actually post; dim
          // the pill (but keep it clickable) while the row is unselected.
          opacity: checked ? 1 : 0.45,
        }}
      >
        {notify ? "🔔 notify" : "direct"}
      </span>
      <ToggleDot on={checked} />
    </button>
  );
}

function ToggleDot({ on }: { on: boolean }): React.JSX.Element {
  return (
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: on ? "linear-gradient(135deg, #FFD500, #FFA500)" : "rgba(255,255,255,.06)",
        border: on ? "none" : "1px solid rgba(255,255,255,.12)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: on ? "0 2px 6px rgba(255,213,0,.30)" : "none",
        transition: "all 140ms ease",
        flexShrink: 0,
      }}
    >
      {on && <Check size={12} color="#1a1a1a" strokeWidth={3.2} />}
    </div>
  );
}

function PlatformBlock({
  service,
  children,
}: {
  service: ChannelService;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      style={{
        padding: 16,
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(255,255,255,.06)",
        borderRadius: 12,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Service accent bar on the left edge for subtle brand identification. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background:
            service === "youtube"
              ? "#FF0033"
              : service === "pinterest"
              ? "#E60023"
              : "rgba(255,255,255,.10)",
        }}
      />
      {children}
    </div>
  );
}

function PlatformLabel({
  service,
  sub,
}: {
  service: ChannelService;
  sub: string;
}): React.JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <ServiceIcon service={service} size={24} />
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ql-fg)", letterSpacing: -0.1 }}>
          {serviceLabel(service)}
        </div>
        <div style={{ fontSize: 11, color: "var(--ql-fg-muted)" }}>{sub}</div>
      </div>
    </div>
  );
}
